import React, { useEffect, useState } from "react";
import {
  Form, Input, DatePicker, TimePicker, Select, Mentions, Button, message,
  Checkbox, Radio, Modal, Table
} from 'antd';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat.js';
dayjs.extend(advancedFormat);
import getOffsetTime from '../../../../utils/getOffsetTime.js';
import timeZones from '../../utils/live-schedules/drop-down-tables/timeZones.js';
import assignmentStatusList from '../../utils/live-schedules/drop-down-tables/assignmentStatusList.js';
import assignmentPriceMethodList from '../../utils/live-schedules/drop-down-tables/assignmentPriceMethodList.js';
import FindCostObject from '../../SelectCostObject/FindCostObject.js';
import AuthLogic from '../../../../AuthLogic/index.js';
import './forms.scss';
import overtimeTypeList from '../../utils/live-schedules/drop-down-tables/overtimeTypeList.js';

const { TextArea } = Input;
const { Option } = Select;

const datePickerFormat = 'h:mm a, MMM Do'
const durationFormat = 'HH:mm';
const FormItem = Form.Item;

const EditAssignmentServicesOrderForm = React.memo(({ loading, resource, itemDetails, user, tableDataSource,
  job, jobElement, costObject, updatingUser, jobElementsWithDetails, groupsByBranchInCompany, service, assignmentServicesByGroup,
  servicesByGroup, activityTypes, serviceSalaryLevel, servicePrimaryRate, serviceJobSalaries, confirmDialogue, ...props
}) => {
  const { itemType } = itemDetails;
  const { getFieldDecorator, getFieldsError, getFieldsValue } = props.form;
  const [selectedRowKeysArray, setSelectedRowKeysArray] = useState([0]);
  const [dirtyTableRows, setDirtyTableRows] = useState(false);
  const [showFindCostObjectForSelectionModal, setShowFindCostObjectForSelectionModal] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [dirtyGroupField, setDirtyGroupField] = useState(false);
  const [overtimeType, setOvertimeType] = useState(false);

  const columns = [
    {
      title: 'Service (Group)',
      dataIndex: 'service',
      key: 'service',
    },
    {
      title: 'Facility',
      dataIndex: 'facility',
      key: 'facility',
    },
    {
      title: 'Resource',
      dataIndex: 'resource',
      key: 'resource',
    },
    {
      title: 'Cost',
      dataIndex: 'cost',
      key: 'cost',
    }
  ];

  const handleSelectedRowChange = async (selectedRowKeys) => {
    setSelectedRowKeysArray(selectedRowKeys);
    let selectedJobElement = jobElementsWithDetails.find((elem) => {
      return elem.id == tableDataSource[selectedRowKeys[0]].id;
    });
    itemDetails.selectedJobElementDetails = selectedJobElement;
    props.getJobElement(selectedJobElement.id); // must call bc it's the only SP with overtimeType & overtimeReason
    await props.updateState({ itemDetails });
  }

  const rowSelection = {
    selectedRowKeys: selectedRowKeysArray, // Table component's selectedRowKeys is always an array
    onChange: handleSelectedRowChange,
    type: 'radio',
    onSelect: async (record, selected) => {
      // console.log(selected);
      // console.log(record);
    }
  };

  const invalidForm = () => {
    message.config({
      top: 150,
      duration: 5,
      maxCount: 5,
    });
    message.error('You have one or more invalid form inputs!');
  };

  const handleSubmit = async (e) => {
    setFormSubmitted(true);
    e.preventDefault();
    const errors = {};
    // collect all fields currently in error state
    for (const [field, error] of Object.entries(getFieldsError())) {
      if (error) {
        errors[`${field}`] = error;
      }
    }
    if (Object.values(errors).length === 0) {
      console.log('Success! Received values of form: ', getFieldsValue())
      props.updateAssignment();
    } else {
      invalidForm();
      console.log('Invalid Form! ', errors);
    }
  };

  const validateAssignmentStartTime = (rule, value, callback) => {
    const form = props.form;
    const assignmentEndTime = form.getFieldValue('assignmentEndTime');
    // *** Note: Not necessary since we're adjusting tourEndTime whenever a change in tourStartTime field
    // if (tourEndTime.isBefore(value)) {
    //   callback('Tour start time cannot occur after tour end time!');
    // } else {
    //   callback();
    // }
  }

  const validateAssignmentEndTime = (rule, value, callback) => {
    const form = props.form;
    const assignmentStartTime = form.getFieldValue('assignmentStartTime');
    if (assignmentStartTime.isAfter(value)) {
      callback('Assignment end time cannot occur before assignment start time!');
    } else {
      callback();
    }
  }

  const validateCommentField = (rule, value, callback) => {
    const form = props.form;
    if (value.length > 100) {
      callback('Maximum length is 100 characters!')
    } else {
      callback();
    }
  }

  const handleSelectCostObjectClick = () => {
    setShowFindCostObjectForSelectionModal(true);
  }

  const handleSelectCostObject = (record) => {
    // input costObjectTypeId field value
    record.costObjectTypeId = record.costObjectType === 'COST_CENTER' ? 2 : 1; // TODO: fix findCostObject so PROJECT_DEFINITION isn't possible
    itemDetails.costObject = {
      costObjectId: record.id,
      costObjectTypeId: record.costObjectTypeId,
      costObjectName: record.costObjectName,
      costObjectNumber: record.costObjectNumber,
      costObjectType: record.costObjectType,
      wbsElementCategory: record.wbsElementCategory
    }
    props.updateState({ itemDetails });
  }

  const handleSelectCostObjectModalCancel = () => {
    setShowFindCostObjectForSelectionModal(false);
  }

  useEffect(() => {
    itemDetails.itemType = 'assignment';
    props.updateState({ itemDetails });
  }, []);

  // itemDetails doesn't arrive until after form has begun to load
  useEffect(() => {
    // if (jobElementsWithDetails) {
    if (!dirtyTableRows) {
      // set jobElement initially selected when tab opens
      let selectedJobElement = jobElement;
      itemDetails.selectedJobElementDetails = selectedJobElement;
      props.updateState({ itemDetails });
    }
    setDirtyTableRows(true);
    // }

  }), [jobElement];

  /**
 * populate fields on initial component mount
 * (getFieldDecorator overrides `value` on forms)
 */
  useEffect(() => {
    if (!dirtyTableRows && itemDetails && jobElementsWithDetails) { // initial form load
      // itemDetails.selectedJobElementDetails = jobElementsWithDetails[itemDetails.initialSelectedRowKey]
    } else if (dirtyTableRows && itemDetails && jobElementsWithDetails) { // form refreshes on selected jobElement
      itemDetails.selectedJobElementDetails = jobElementsWithDetails.find((elem) => {
        return elem.id == tableDataSource[selectedRowKeysArray[0]].id;
      });

      // jobElementsWithDetails[selectedRowKeysArray[0]];
    }

    if (itemDetails.selectedJobElementDetails && service && costObject) {
      // update main itemDetails object w/ details of selected jobElement (for ease of saving once done editing)
      itemDetails.itemType = 'assignment';
      itemDetails.jobElementId = itemDetails.selectedJobElementDetails.id;
      itemDetails.jobId = itemDetails.selectedJobElementDetails.jobId;
      itemDetails.tourId = itemDetails.selectedJobElementDetails.tourId;
      itemDetails.resourceId = itemDetails.selectedJobElementDetails.resourceId;
      itemDetails.executingBranchId = itemDetails.selectedJobElementDetails.executingBranchId;
      itemDetails.groupId = service.groupId;
      itemDetails.serviceId = itemDetails.selectedJobElementDetails.serviceId;
      itemDetails.relatedServiceId = itemDetails.selectedJobElementDetails.relatedServiceId;
      itemDetails.status = itemDetails.selectedJobElementDetails.status;
      itemDetails.startRequest = itemDetails.selectedJobElementDetails.startRequest;
      itemDetails.endRequest = itemDetails.selectedJobElementDetails.endRequest;
      itemDetails.startActual = itemDetails.selectedJobElementDetails.startActual;
      itemDetails.endActual = itemDetails.selectedJobElementDetails.endActual;
      itemDetails.overtimeType = jobElement.overtimeType;
      itemDetails.overtimeReason = jobElement.overtimeReason;

      // timezone offset adjustment
      let startTime = dayjs(itemDetails.selectedJobElementDetails.startActual);
      let timeZoneOffset = getOffsetTime(startTime);
      let assignmentStartTime = dayjs(itemDetails.selectedJobElementDetails.startActual).add(timeZoneOffset, 'minute');
      let assignmentEndTime = dayjs(itemDetails.selectedJobElementDetails.endActual).add(timeZoneOffset, 'minute');


      // update starte/end time
      itemDetails.assignmentStartTime = assignmentStartTime;
      itemDetails.assignmentEndTime = assignmentEndTime;
      props.generateItemDuration('assignment', assignmentStartTime, assignmentEndTime);

      itemDetails.timeZoneId = itemDetails.selectedJobElementDetails.timeZoneId;
      itemDetails.orderingUserId = itemDetails.selectedJobElementDetails.orderingUserId;
      itemDetails.updatingUserId = itemDetails.selectedJobElementDetails.updatingUserId;
      itemDetails.descriptionComment = itemDetails.selectedJobElementDetails.comment;
      itemDetails.approverComment = itemDetails.selectedJobElementDetails.approverComment;
      itemDetails.employeeComment = itemDetails.selectedJobElementDetails.employeeComment;
      itemDetails.schedulerComment = itemDetails.selectedJobElementDetails.schedulerComment;
      itemDetails.isAnUpgrade = itemDetails.selectedJobElementDetails.isAnUpgrade ? itemDetails.selectedJobElementDetails.isAnUpgrade : false;
      itemDetails.overtime = itemDetails.selectedJobElementDetails.overTime;
      itemDetails.overtimeType = jobElement.overtimeType;
      itemDetails.overtimeReason = jobElement.overtimeReason;
      itemDetails.activityType = itemDetails.selectedJobElementDetails.activityType ? itemDetails.selectedJobElementDetails.activityType.trim() : null;
      itemDetails.costObject = {
        costObjectId: itemDetails.selectedJobElementDetails.costObjectId,
        costObjectTypeId: itemDetails.selectedJobElementDetails.costObjectTypeId,
        costObjectName: costObject.costObjectName,
        costObjectType: costObject.costObjectType,
        costObjectNumber: costObject.costObjectNumber
      };
      itemDetails.jobSalaryUpgradeId = itemDetails.selectedJobElementDetails.jobSalaryUpgradeId;
      itemDetails.hourlyActivityRateFlag = itemDetails.selectedJobElementDetails.hourlyActivityRateFlag;
      itemDetails.rate = itemDetails.selectedJobElementDetails.rate;
    }

    // no matter what, update state
    props.updateState({ itemDetails });

  }, [dirtyTableRows, itemDetails, jobElementsWithDetails, itemDetails.selectedJobElementDetails, service, costObject, jobElement, selectedRowKeysArray]);

  // start/end time form field hooks
  useEffect(() => {
    const form = props.form;
    // only necessary for times and comment fields (non-controlled fields)
    form.setFieldsValue({
      assignmentStartTime: itemDetails.assignmentStartTime,
      assignmentDuration: itemDetails.assignmentDuration,
      assignmentEndTime: itemDetails.assignmentEndTime,
      descriptionComment: itemDetails.descriptionComment,
      approverComment: itemDetails.approverComment,
    });
    if (overtimeType) {
      form.setFieldsValue({
        overtimeReason: itemDetails.overtimeReason
      });
    }

  }, [itemDetails, itemDetails.assignmentDuration, itemDetails.assignmentStartTime, itemDetails.assignmentEndTime, jobElement, overtimeType, selectedRowKeysArray]);

  // hook for group dropdown, dependent on executingBranchId field in jobElementsWithDetails
  useEffect(() => {
    if (itemDetails && itemDetails.selectedJobElementDetails && itemDetails.selectedJobElementDetails.executingBranchId) {
      props.getGroupsByBranchInCompany();
      props.updateState({ groupsByBranchInCompany });
    } else {
      // clear group dropdown
      props.handleOmnibusFieldChange(`groupId-${null}`);
      groupsByBranchInCompany = null;
      props.updateState({ groupsByBranchInCompany });
    }
  }, [itemDetails, jobElementsWithDetails, selectedRowKeysArray]);

  // hook to get service information once jobElement has been selected
  useEffect(() => {
    // service selection is dependent on serviceId
    if (itemDetails && itemDetails.selectedJobElementDetails && // jobElement selected but service api not called yet
      itemDetails.selectedJobElementDetails.serviceId && service
    ) {
      props.getService();
      props.updateState({ service })
    }
  }, [itemDetails, jobElementsWithDetails, selectedRowKeysArray]); // don't put item that you're updating in state in dep. array --> infinite update loop

  // hook for service dropdown, dependent on groupId from service result set and serviceId from jobElementsWithDetails..
  // Note: also runs every time groupId is updated via its dropdown
  useEffect(() => {
    if (itemDetails && itemDetails.selectedJobElementDetails
      && itemDetails.selectedJobElementDetails.serviceId
      && service
    ) {
      if (itemDetails.groupId !== service.groupId
        || dirtyGroupField
      ) {
        // changeed group dropdown
        // clear service field
        props.handleOmnibusFieldChange(`serviceId-${null}`);
        props.getServicesByGroup();
        setDirtyGroupField(true);
      } else {
        props.getAssignmentServicesByGroup();
      }
    } else {
      // clear service dropdown
      props.handleOmnibusFieldChange(`serviceId-${null}`);
      assignmentServicesByGroup = null;
      props.updateState({ assignmentServicesByGroup });
    }
  }, [itemDetails, itemDetails.groupId, jobElementsWithDetails, service, selectedRowKeysArray]);

  // activityType dropdown hook
  useEffect(() => {
    // activityType is dependent on assignmentStartTime and relatedServiceId
    if (itemDetails && itemDetails.selectedJobElementDetails &&
      itemDetails.selectedJobElementDetails.relatedServiceId &&
      itemDetails.selectedJobElementDetails.startActual) {
      props.getActivityTypes();
      props.updateState({ activityTypes });
    } else {
      // clear activityType field and activityTypes dropdown list
      props.handleOmnibusFieldChange(`activityType-${null}`);
      activityTypes = null;
      props.updateState({ activityTypes });
    }
  }, [itemDetails, jobElementsWithDetails, selectedRowKeysArray]);

  // service serviceSalaryLevel and servicePrimaryRate dropdown hook
  useEffect(() => {
    if (itemDetails && itemDetails.selectedJobElementDetails &&
      itemDetails.selectedJobElementDetails.relatedServiceId) {
      props.getServiceSalaryLevel();
      props.getServicePrimaryRate();
      props.updateState({
        serviceSalaryLevel,
        servicePrimaryRate
      });
    } else {
      // clear serviceSalaryLevel and servicePrimaryRate fields
      props.handleOmnibusFieldChange(`servicePrimaryRate-${null}`);
      serviceSalaryLevel = null;
      servicePrimaryRate = null;
      props.updateState({
        serviceSalaryLevel,
        servicePrimaryRate
      });
    }
  }, [itemDetails, jobElementsWithDetails, service, selectedRowKeysArray]);

  // jobSalaryUpgradeId dropdown hook
  useEffect(() => {
    if (itemDetails && itemDetails.selectedJobElementDetails &&
      itemDetails.selectedJobElementDetails.serviceId &&
      itemDetails.selectedJobElementDetails.startActual
    ) {
      props.getJobSalariesByService();
      props.updateState({ serviceJobSalaries });
    } else {
      // clear jobSalaryUpgradeId field
      props.handleOmnibusFieldChange(`jobSalaryUpgradeId-${0}`);
      serviceJobSalaries = null;
      props.updateState({ serviceJobSalaries });
    }
  }, [itemDetails, jobElementsWithDetails, selectedRowKeysArray]);

  // appear overtimeReason text box if an overtimeType is selected
  useEffect(() => {
    if (itemDetails.overtimeType && itemDetails.overtimeType != 0) {
      setOvertimeType(true);
    }
  }, [itemDetails.overtimeType]);

  // clear overtimeReason if overtimeType is null or None
  useEffect(() => {
    if (itemDetails
      && (
        !itemDetails.overtimeType
        || (itemDetails.overtimeType && itemDetails.overtimeType == 0)
      )
    ) {
      setOvertimeType(false);
      itemDetails.overtimeReason = null;
      props.updateState({ itemDetails })
    }
  }, [itemDetails.overtimeType]);

  if (itemDetails /* && itemDetails.selectedJobElementDetails */ && job && costObject && updatingUser &&
    jobElementsWithDetails && !loading
  ) {
    console.log(`dirtyTableRows: ${dirtyTableRows}`); // debug
    console.log(itemDetails.initialSelectedRowKey); // debug
    console.log(`selectedRowKeysArray: ${selectedRowKeysArray}`) // debug

    // format job.baseDate into (MM/DD/YYYY)
    let baseDate;
    if (job.baseDate) {
      baseDate = job.baseDate.split('T')[0];
      baseDate = baseDate.split('-');
      baseDate = `${baseDate[1]}/${baseDate[2]}/${baseDate[0]}`;
    }

    // adjust startRequest and endRequest per timezone for text
    let timeZoneOffset = getOffsetTime(dayjs(job.baseDate));
    let startRequest = dayjs(job.startRequest).add(timeZoneOffset, 'minute');
    let endRequest = dayjs(job.endRequest).add(timeZoneOffset, 'minute');

    return (

      <Form onSubmit={handleSubmit}>
        <div>
          Request Date: <b><u>{baseDate}</u></b> <br />
          {`Request #: ${job.jobNumber}: ${job.name}`}<br />
          {`Cost Object #: ${costObject.costObjectNumber} ${costObject.costObjectName} ${costObject.costObjectType}`}<br />
          {`Request Date Range: ${startRequest.format('MM/DD/YYYY')} - ${endRequest.format('MM/DD/YYYY')}`}<br />
          {`Modified By: ${updatingUser.name}`}
        </div>

        <Form.Item>
          <Table
            dataSource={tableDataSource}
            columns={columns}
            rowSelection={rowSelection}
          // rowClassName={(record) =>  record.color ? record.color.replace('#', '') : null}
          // onRow={(record) => record.key === selectedRowKeysArray[0] ? record.color = 'greenRow' : null}
          // ßrowClassName={(record) => ''}
          />
        </Form.Item>

        <Form.Item
          label="Start Time"
          style={{ display: "flex", marginBottom: 5 }}
        >
          {getFieldDecorator('assignmentStartTime', {
            rules: [{
              required: true, message: 'Please input start time!',
            }, {
              validator: validateAssignmentStartTime,
            }],
          })(
            <DatePicker
              name="assignmentStartTime"
              showTime={{ format: datePickerFormat, use12Hours: true, minuteStep: 15 }}
              onChange={props.handleItemStartTimeChange}
              placeholder={'Select date and time'}
              format={datePickerFormat}
              allowClear={false}
              size={'large'}
              styles={{
                popup: {
                  root: {
                    zIndex: 2000
                  }
                }
              }}
            />
          )}
        </Form.Item>

        <Form.Item
          label="Duration"
          style={{ display: "flex", marginBottom: 5 }}
        >
          {itemDetails.assignmentDuration && itemDetails.assignmentDurationDays && itemDetails.assignmentDurationDays.days &&
            (
              <span>
                <Mentions
                  value={itemDetails.assignmentDurationDays.days}
                  readOnly
                  style={{ marginLeft: 10, marginRight: 5, width: 50 }}
                />
                <span>day(s) &</span>
              </span>
            )
          }
          {getFieldDecorator('assignmentDuration', {
            rules: [{
              required: true, message: 'Duration is required!',
              type: 'object'
            }],
          })(
            <TimePicker
              name="assignmentDuration"
              onChange={props.handleItemDurationChange}
              allowClear={false}
              format={durationFormat}
              minuteStep={15}
              style={{ marginLeft: 5, marginRight: 5 }}
              showNow={false}
            />
          )}
          hours
        </Form.Item>

        <Form.Item
          label="End Time"
          style={{ display: "flex", marginBottom: 5 }}
        >
          {getFieldDecorator('assignmentEndTime', {
            rules: [{
              required: true, message: 'Please input end time!',
            }, {
              validator: validateAssignmentEndTime,
            }],
          })(
            <DatePicker
              name="assignmentEndTime"
              showTime={{ format: datePickerFormat, use12Hours: true, minuteStep: 15 }}
              onChange={props.handleItemEndTimeChange}
              placeholder={'Select date and time'}
              format={datePickerFormat}
              size={'large'}
              styles={{
                popup: {
                  root: {
                    zIndex: 2000
                  }
                }
              }}
            />
          )}
        </Form.Item>

        <Form.Item
          label={<span><span id="asterisk">* </span><span>Group</span></span>}
          style={{ display: "flex", marginBottom: 5 }}
        >
          <Select
            name="groupId"
            value={itemDetails.groupId ? `groupId-${itemDetails.groupId.toString()}` : `groupId-${null}`} // necessary to clear value dynamically
            // disabled={!itemDetails.selectedJobElementDetails.executingBranchId}
            onChange={props.handleOmnibusFieldChange}
            style={{ width: 230 }}
          >
            <Option key={null} value={`groupId-${null}`}>&nbsp;</Option>

            {groupsByBranchInCompany &&
              groupsByBranchInCompany.map((elem) => {
                return <Option key={elem.id} value={`groupId-${elem.id}`}>{elem.name}</Option>
              })}
          </Select>
        </Form.Item>

        <Form.Item
          label={<span><span id="asterisk">* </span><span>Service</span></span>}
          style={{ display: "flex", marginBottom: 5 }}
        >
          <Select
            name="serviceId"
            value={`serviceId-${itemDetails.serviceId.toString()}`}
            // disabled={!itemDetails.selectedJobElementDetails.serviceId || !service}
            onChange={props.handleOmnibusFieldChange}
            style={{ width: 400 }}
          >
            <Option key={null} value={`serviceId-${null}`}>&nbsp;</Option>

            {!dirtyGroupField &&
              assignmentServicesByGroup &&
              assignmentServicesByGroup.map((elem) => {
                return <Option key={elem.id} value={`serviceId-${elem.id}`}>{elem.name}</Option>
              })}

            {dirtyGroupField &&
              servicesByGroup &&
              servicesByGroup.map((elem) => {
                return <Option key={elem.id} value={`serviceId-${elem.id}`}>{elem.name}</Option>
              })}

          </Select>
        </Form.Item>

        <Form.Item
          label={<span><span id="asterisk">* </span><span>Activity Type</span></span>}
          style={{ display: "flex", marginBottom: 5 }}
        >
          <Select
            name="activityType"
            value={`activityType-${itemDetails.activityType ? itemDetails.activityType.toString().trim() : null}`}
            // disabled={!itemDetails.selectedJobElementDetails.relatedServiceId
            //   || !itemDetails.selectedJobElementDetails.startActual}
            onChange={props.handleOmnibusFieldChange}
            style={{ width: 350 }}
          >
            <Option key={null} value={`activityType-${null}`}>&nbsp;</Option>

            {activityTypes &&
              activityTypes.map((elem) => {
                return <Option key={elem.activityType} value={`activityType-${elem.activityType.trim()}`}>{elem.description.split('VIDEO')[0] + elem.rates}</Option>
              })}
          </Select>
        </Form.Item>

        {AuthLogic.addAssignmentViewSalaryLevel(user) &&
          <Form.Item
            label="Salary Level"
            style={{ display: "flex", marginBottom: 5 }}
          >
            {
              serviceSalaryLevel ? serviceSalaryLevel : ' '
            }
          </Form.Item>
        }


        <Form.Item
          label="" // leave blank
          style={{ display: "flex", marginBottom: 5 }}
        >
          <Select
            name="jobSalaryUpgradeId"
            value={`jobSalaryUpgradeId-${itemDetails.jobSalaryUpgradeId}`}
            onChange={props.handleOmnibusFieldChange}
            style={{ width: 120, marginBottom: 5 }}
          >

            <Option key={0} value={`jobSalaryUpgradeId-${0}`}>&nbsp;</Option>

            {serviceJobSalaries &&
              serviceJobSalaries.map((elem) => {
                return (
                  <Option
                    style={{ color: elem.primaryRate <= servicePrimaryRate ? 'red' : 'black' }}
                    key={elem.id} value={`jobSalaryUpgradeId-${elem.id}`}
                  >
                    {elem.name}
                  </Option>
                )
              })}
          </Select>
        </Form.Item>

        <Form.Item
          label="Assignment is upgraded"
          style={{ display: "flex", marginBottom: 5 }}
        >
          <Checkbox
            name="upgrade"
            // onChange={props.handleOmnibusFieldChange}  // currently disabled in storm
            checked={false}
            size={'large'}
          />
        </Form.Item>

        <Form.Item
          label="Price Method"
          style={{ display: "flex", marginBottom: 5 }}
        >
          <Select
            name="priceMethodId"
            defaultValue={`priceMethodId-${user.securityLevels.defaultPriceMethod}`} // default is Rate Card = 2
            onChange={props.handleOmnibusFieldChange}
            style={{ width: 120 }}
          >
            {assignmentPriceMethodList.map((elem) => {
              return <Option key={elem.id} value={`priceMethodId-${elem.id}`}>{elem.name}</Option>
            })}
          </Select>
        </Form.Item>

        {AuthLogic.addAssignmentEditAssignmentStatus(user) &&
          <Form.Item
            label="Status"
            style={{ display: "flex", marginBottom: 5 }}
          >
            <Select
              name="status"
              defaultValue={`status-${itemDetails.status.toString()}`} // default is standby = 10
              onChange={props.handleOmnibusFieldChange}
              style={{ width: 120 }}
            >
              {assignmentStatusList.map((status) => {
                return <Option key={status.id} value={`status-${status.id}`}>{status.name}</Option>
              })}
            </Select>
          </Form.Item>
        }

        <span id="asterisk">* </span>
        <a
          onClick={() => handleSelectCostObjectClick()}
          style={{ color: 'dodgerblue', textDecoration: 'underline', cursor: 'pointer' }}
        >
          Cost Object:
        </a>

        {`\xa0`}{`\xa0`}{`\xa0`}
        {itemDetails.costObject &&
          `${itemDetails.costObject.costObjectName}
                  ${`\xa0`}
                  ${itemDetails.costObject.costObjectType === 'WBS_ELEMENT'
            || itemDetails.costObject.costObjectType === 'PROJECT_DEFINITION'
            ? 'WBS Element' : itemDetails.costObject.costObjectType || 'Not specified'}`}
        {`\xa0`}

        {showFindCostObjectForSelectionModal &&
          <Modal
            open={showFindCostObjectForSelectionModal}
            title={'Find Cost Object'}
            onCancel={handleSelectCostObjectModalCancel}
            footer={[
            ]}
          >
            <FindCostObject
              requestDate={itemDetails.selectedJobElementDetails.startActual}
              handleSelectCostObject={handleSelectCostObject}
              handleModalCancel={handleSelectCostObjectModalCancel}
            />
          </Modal>
        }

        {AuthLogic.addAssignmentEditDescription(user) &&
          <Form.Item
            label="Description"
            style={{ display: "flex", marginBottom: 5 }}
          >
            {getFieldDecorator('descriptionComment', {
              rules: [{
                validator: validateCommentField,
              }],
            })(
              <TextArea
                name="descriptionComment"
                onChange={props.handleOmnibusFieldChange}
                size={'large'}
                placeholder={'Enter text here.'}
                style={{ width: 300 }} />
            )}
          </Form.Item>
        }

        <Form.Item
          label="Overtime"
          style={{ display: "flex", marginBottom: 5 }}
        >
          <Checkbox
            name="overtime"
            checked={itemDetails.overtime}
            onChange={props.handleOmnibusFieldChange}
            size={'large'}
          />
        </Form.Item>

        <Form.Item
          label="Overtime Type"
          style={{ display: "flex", marginBottom: 5 }}
        >
          <Select
            name="overtimeType"
            value={`overtimeType-${itemDetails.overtimeType}`}
            defaultValue={`overtimeType-${0}`}
            onChange={props.handleOmnibusFieldChange}
            style={{ width: 210 }}
          >

            {overtimeTypeList.map((type) => {
              return <Option key={type.id} value={`overtimeType-${type.id}`}>{type.label}</Option>
            })}
          </Select>
        </Form.Item>

        {overtimeType && (itemDetails.overtimeType != 0) &&

          <Form.Item
            label="Overtime Reason"
            style={{ display: "flex", marginBottom: 5 }}
          >
            {getFieldDecorator('overtimeReason', {
              rules: [{
                validator: validateCommentField,
              }],
            })(
              <Input
                name="overtimeReason"
                onChange={props.handleOmnibusFieldChange}
                size={'large'}
                placeholder={'Enter text here.'}
                style={{ width: 300 }} />
            )}
          </Form.Item>
        }



        {AuthLogic.addAssignmentEditDescription(user) && // TODO: replace this with approverComment security logic
          <Form.Item
            label="Approver Comment"
            style={{ display: "flex", marginBottom: 5 }}
          >
            {getFieldDecorator('approverComment', {
              rules: [{
                validator: validateCommentField,
              }],
            })(
              <Input
                name="approverComment"
                onChange={props.handleOmnibusFieldChange}
                size={'large'}
                placeholder={'Enter text here.'}
                style={{ width: 300 }} />
            )}
          </Form.Item>
        }

        <Form.Item
          style={{ marginTop: 20 }}
        >
          <Button type="primary" ghost block size={'large'} htmlType="submit" disabled={formSubmitted} >
            {serviceJobSalaries && !formSubmitted ? `Update` : formSubmitted ? 'Updating...' : `loading...`}
          </Button>
        </Form.Item>

      </Form>
    );
  } else return 'loading...'
})

export default EditAssignmentServicesOrderForm;

